import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import seo from '../../services/seo';
import BOA_CONFIG from '../../constants/BOAConfig';
import * as analytics from '../../services/analytics';
import LandingBlockPartner from '../LandingBlockPartner';
import ModalPortal from '../ModalPortal';
import VERTICAL_CONFIG from '../../constants/verticalConfig';
import isEmpty from 'lodash/isEmpty';
import imageChubbLogo from './chubb-logo-pink.png';
import imageAmbulance from './ambulance.jpg';
import imageWheelchair from './wheelchair.jpg';
import imagePaperwork from './paperwork.jpg';
import './LandingBOA.css';

const propTypes = {
  quote: PropTypes.object,
};

const defaultProps = {
  quote: {},
};

class LandingBOA extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      activeModal: null,
    };
  }

  componentDidMount() {
    analytics.page('Broker Portal - BOA Landing');
    seo.setProps({
      title: 'Business Owners Accident',
    });
  }

  handleUnavailableVertical = () => {
    if (!VERTICAL_CONFIG.IS_BOA_ENABLED) {
      const modalId = 'unavailableModal';
      this.setState(
        {
          activeModal: modalId,
        },
        () => {
          const $modal = window.$(this[modalId]);
          $modal.modal('show');
          $modal.on('hide.bs.modal', () => {
            this.setState({
              activeModal: null,
            });
          });
        }
      );
    }
  };

  handleLinkCTA = () => {
    const { quote, location } = this.props;
    if (VERTICAL_CONFIG.IS_BOA_ENABLED) {
      if (isEmpty(quote)) {
        return {
          pathname: `business-owners-accident/start/policyholder`,
          search: location.search,
        };
      } else {
        return `boa/enroll/${quote.quote_number}/business-region`;
      }
    }
  };

  trackDocClick = (e) => {
    analytics.track('Broker Portal - External Link Clicked', {
      label: e.target.dataset.key,
    });
  };

  render() {
    const { activeModal } = this.state;
    return (
      <div className="LandingBOA">
        <div
          className="LandingBOA-hero"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${process.env.PUBLIC_URL}/img/landing-boa-hero@2x.png)`,
          }}
        >
          <h1 className="LandingBOA-hero-heading">
            As a business owner you cannot afford to risk what may happen to you
            or your employees if there is an injury from a serious accident.
            That’s why Chubb offers you the Business Owners Accident Protection
            Plan, specifically designed for business owners like you.
          </h1>
          <Link
            className="LandingBOA-hero-cta btn btn-primary btn-large"
            data-testid="heading_check_our_prices_button"
            onClick={this.handleUnavailableVertical}
            to={this.handleLinkCTA}
          >
            Check Our Prices
          </Link>
        </div>

        <div className="LandingBOA-tagline">
          The Advantages of Business Owners Accident Insurance
        </div>

        <div className="LandingBOA-highlightsTwoUps">
          <div className="container">
            <div className="row">
              <div className="LandingBOA-twoUp col-md-6">
                <div className="LandingBOA-twoUp-heading">
                  <div className="LandingBOA-twoUp-heading-icon">
                    <i className="far fa-user-tie" />
                  </div>
                  <p className="LandingBOA-twoUp-heading-title">
                    For Business Owners
                  </p>
                </div>
                <p className="LandingBOA-twoUp-info">
                  A simple, affordable way to help provide the critical
                  financial security needed when a serious accident happens.
                  Taking advantage of group rates allows you to purchase high
                  limit Accidental Death and Dismemberment at a fraction of the
                  cost you could find in the individual market. You’ll also
                  enjoy the peace of mind that comes in knowing you are helping
                  to protect your employees when they are at the office or out
                  representing your business.
                </p>
              </div>
              <div className="LandingBOA-twoUp col-md-6">
                <div className="LandingBOA-twoUp-heading">
                  <div className="LandingBOA-twoUp-heading-icon">
                    <i className="far fa-users" />
                  </div>
                  <p className="LandingBOA-twoUp-heading-title">
                    For Employees
                  </p>
                </div>
                <p className="LandingBOA-twoUp-info">
                  Benefits that pay in a lump sum directly to the employee
                  without regard to any other insurance that may be payable.
                  These benefits may be used to help defray out-of-pocket
                  medical expenses, to supplement disability benefits or even to
                  put away for other rainy day expenses.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="LandingBOA-details">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="LandingBOA-details-heading">
                  What does Business Owners Accident Cover?
                </h2>
              </div>
            </div>
            <div className="row">
              <div className="LandingBOA-details-pane col-md-4">
                <img
                  className="LandingBOA-details-image m-b-15"
                  src={imageAmbulance}
                  alt="An ambulance"
                />
                <h3 className="LandingBOA-details-itemHeading">
                  Accidental Death and Severe Injury
                </h3>
                <p className="LandingBOA-details-itemBody">
                  In the event of a covered loss the Insured Person is covered
                  for Accidental Loss of Life, Limb, Speech, Hearing, and
                  Paralysis.
                </p>
              </div>
              <div className="LandingBOA-details-pane col-md-4">
                <img
                  className="LandingBOA-details-image m-b-15"
                  src={imageWheelchair}
                  alt="A wheelchair"
                />
                <h3 className="LandingBOA-details-itemHeading">
                  Accidental Disability
                </h3>
                <p className="LandingBOA-details-itemBody">
                  In the event of a Felonious Assault the Insured Person is
                  covered up to $250,000 of Permanent Total Disability and a
                  total of $13,000 in Temporary Disability income if the injury
                  causes them to miss work.
                </p>
              </div>
              <div className="LandingBOA-details-pane col-md-4">
                <img
                  className="LandingBOA-details-image m-b-15"
                  src={imagePaperwork}
                  alt="Paperwork"
                />
                <h3 className="LandingBOA-details-itemHeading">
                  Cobra Premium Expense
                </h3>
                <p className="LandingBOA-details-itemBody">
                  In the event of a covered loss of life this benefit pays up to
                  $10,000 annually for the Insured Person's Dependent who is
                  COBRA eligible to continue group medical or group dental
                  insurance under a plan provided through the Policyholder.
                </p>
              </div>
            </div>
          </div>
        </div>

        <LandingBlockPartner partnerName="Chubb" logo={imageChubbLogo}>
          <p>
            Chubb Accident Insurance leverages superior underwriting expertise
            with the world renowned claims, account services, and financial
            strength that you expect from Chubb.
          </p>
          <p>
            Chubb is the world’s largest publicly traded P&C insurance company
            and the largest commercial insurer in the U.S.
          </p>
        </LandingBlockPartner>

        <div className="LandingBOA-banner">
          <div className="container">
            <h3 className="LandingBOA-banner-heading">
              Protect yourself and your employees today!
            </h3>
            <Link
              className="LandingBOA-banner-cta btn btn-primary btn-large"
              data-testid="banner_check_our_prices_button"
              onClick={this.handleUnavailableVertical}
              to={this.handleLinkCTA}
            >
              Check Our Prices
            </Link>
          </div>
        </div>

        <div className="LandingBOA-footer">
          <div className="LandingBOA-footer-content">
            <p className="LandingBOA-footer-faq">
              Need help? Click to download and/or view{' '}
              <a
                href={BOA_CONFIG.planOverviewUrl}
                className="text-complete"
                target="_blank"
                rel="noopener noreferrer"
                data-key="planOverview"
                onClick={this.trackDocClick}
              >
                product brochure
              </a>
              .
            </p>
            <p className="LandingBOA-footer-disclaimer">
              This information is a brief description of the important features
              of this insurance plan. It is not an insurance contract. Insurance
              benefits are underwritten by Federal Insurance Company. Coverage
              may not be available in all states or certain terms may be
              different where required by state law. Chubb NA is the U.S.-based
              operating division of the Chubb Group of Companies, headed by
              Chubb Ltd. (NYSE:CB) Insurance products and services are provided
              by Chubb Insurance underwriting companies and not by the parent
              company itself.
            </p>
          </div>
        </div>
        {activeModal === 'unavailableModal' && (
          <ModalPortal>
            <div
              className="modal"
              id="unavailableModal"
              ref={(c) => (this.unavailableModal = c)}
            >
              <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content-wrapper">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Business Owners Accident</h5>
                      <button
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="modal-body">
                      <p>
                        Business Owners Accident is temporarily unavailable.
                        Please try again later.
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="submit"
                        className="btn btn-complete"
                        data-dismiss="modal"
                      >
                        Ok
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ModalPortal>
        )}
      </div>
    );
  }
}

LandingBOA.propTypes = propTypes;
LandingBOA.defaultProps = defaultProps;

export default LandingBOA;
